import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { brlToInt, intToBrl } from "../../utils";
import Api from "../../Api";
import _ from "lodash";
import classNames from "classnames";
import Section from "../Section";
import MoneyInput from "../MoneyInput";
import { Category, Equipment, QuoteItem, UIValue } from "../../types";
import { QuoteAsForm, Quote } from "../../types";
import DiscreetMessage from "../DiscreetMessage";

type PriceMapItem = { price: number; rent: number };
type PriceMap = Record<string, PriceMapItem>;

type QuoteFormProps = {
  quote: QuoteAsForm;
  onChange: (key: string) => (value: UIValue | QuoteItem[]) => void;
  onSave: (quote: Quote) => Promise<void>;
  buttonLabel: string;
  error: boolean;
};
const QuoteForm = ({
  quote,
  onChange,
  onSave,
  buttonLabel,
  error,
}: QuoteFormProps) => {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [equipments, setEquipments] = useState<Equipment[] | null>(null);
  const [priceMap, setPriceMap] = useState<PriceMap | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Api.myQuotesGetCategories().then(setCategories);
    Api.myQuotesGetEquipments().then((equipments) => [
      setEquipments(equipments),
      setPriceMap(
        _.chain(equipments)
          .keyBy("uuid")
          .mapValues((equip) => ({ price: equip.price, rent: equip.rent }))
          .valueOf()
      ),
    ]);
  }, []);

  const addItem = (type: "sale" | "rent") => () => {
    const setter =
      type === "sale" ? onChange("itemsSold") : onChange("itemsRented");
    const items = type === "sale" ? quote.itemsSold : quote.itemsRented;
    setter([...items, { equipmentUuid: "", amount: 1, price: 0 }]);
  };

  const deleteItem = (type: "sale" | "rent", index: number) => () => {
    const setter =
      type === "sale" ? onChange("itemsSold") : onChange("itemsRented");
    const items = type === "sale" ? quote.itemsSold : quote.itemsRented;
    setter([...items.slice(0, index), ...items.slice(index + 1)]);
  };

  const updateItem =
    (type: "sale" | "rent", index: number) =>
    (prop: string, value: UIValue) => {
      const setter =
        type === "sale" ? onChange("itemsSold") : onChange("itemsRented");
      const items = type === "sale" ? quote.itemsSold : quote.itemsRented;
      setter([
        ...items.slice(0, index),
        { ...items[index], [prop]: value },
        ...items.slice(index + 1),
      ]);
    };

  const illegalDiscount =
    !priceMap ||
    getPrice(priceMap, quote, "sale", "discount") < 0 ||
    getPrice(priceMap, quote, "rent", "discount") < 0;

  const convert = (to: "sale" | "rent") => () => {
    const uuids = _.chain(to === "sale" ? quote.itemsRented : quote.itemsSold)
      .map("equipmentUuid")
      .filter()
      .map((uuid) => equipments!.find((equip) => equip.uuid === uuid)!)
      .filter((equip) => (to === "sale" ? equip.canSell : equip.canRent))
      .map("uuid")
      .valueOf();

    const fromItems = to === "sale" ? quote.itemsRented : quote.itemsSold;
    const toItems = to === "sale" ? quote.itemsSold : quote.itemsRented;

    const additions: QuoteItem[] = uuids.map((uuid) => ({
      equipmentUuid: uuid,
      amount: fromItems.find((item) => item.equipmentUuid === uuid)!.amount,
      price: priceMap![uuid][to === "sale" ? "price" : "rent"],
    }));

    onChange(to === "sale" ? "itemsSold" : "itemsRented")([
      ...toItems,
      ...additions,
    ]);

    onChange(to === "sale" ? "itemsRented" : "itemsSold")([
      ...fromItems.filter((item) => !uuids.includes(item.equipmentUuid)),
    ]);
  };

  const save = async () => {
    setSaving(true);
    await onSave({
      uuid: "",
      clientUuid: "",
      number: 0,
      externalHash: "",
      creationDate: new Date(),
      isApproved: false,
      approvalDate: null,
      isAuthorized: false,
      authorizationDate: null,
      itemsSold: _.reject(quote.itemsSold, { equipmentUuid: "" }),
      itemsRented: _.reject(quote.itemsRented, { equipmentUuid: "" }),
      feeMonitoring: brlToInt(quote.feeMonitoring),
      feeInstall: brlToInt(quote.feeInstall),
      feeReview: brlToInt(quote.feeReview),
      maxInstallments: parseInt(quote.maxInstallments),
      discountSale: brlToInt(quote.discountSale),
      discountRent: brlToInt(quote.discountRent),
      note: quote.note,
      imported: false,
      sends: [],
      authorizedByClient: null,
      isCancelled: false,
      cancellationDate: null,
      notes: [],
      tasks: {
        signature: false,
        order: false,
        equipment: false,
        install: false,
        billing: false,
        commission: false,
      },
      push: [],
      rentContractInfo: null,
      monitoringContractInfo: null,
    });
    setSaving(false);
  };

  if (!categories || !equipments) {
    return <DiscreetMessage text="Carregando ..." />;
  }

  return (
    <Section className="p-4 flex flex-col gap-4 pb-32">
      <EquipmentTable
        type="sale"
        items={quote.itemsSold}
        categories={categories}
        equipments={_.filter(equipments, { canSell: true })}
        onUpdate={(index) => updateItem("sale", index)}
        onDelete={(index) => deleteItem("sale", index)}
        onAdd={addItem("sale")}
      />

      <EquipmentTable
        type="rent"
        items={quote.itemsRented}
        categories={categories}
        equipments={_.filter(equipments, { canRent: true })}
        onUpdate={(index) => updateItem("rent", index)}
        onDelete={(index) => deleteItem("rent", index)}
        onAdd={addItem("rent")}
      />

      <Fees quote={quote} onChange={onChange} />

      <hr />

      <div>
        <h1 className="font-semibold mb-2">Observações</h1>
        <textarea
          className="w-full border rounded p-2 text-sm h-24"
          value={quote.note}
          onChange={(e) => onChange("note")(e.target.value)}
        ></textarea>
      </div>

      {priceMap && (
        <Summary quote={quote} priceMap={priceMap} onChange={onChange} />
      )}

      <Converter convert={convert} />

      {error && (
        <div className="text-sm text-red-700 font-bold text-center bg-red-50 py-2 rounded-xl -mb-2">
          Erro ao salvar orçamento
        </div>
      )}

      <div>
        <button
          className={classNames("rounded py-2 px-4 w-full ", {
            "bg-sky-600 hover:bg-sky-500 text-white font-extrabold":
              !illegalDiscount,
            "font-bold bg-gray-100 text-gray-500": illegalDiscount,
          })}
          onClick={save}
          disabled={illegalDiscount || saving}
        >
          {buttonLabel}
        </button>
      </div>
    </Section>
  );
};

export default QuoteForm;

const EquipmentTable = ({
  type,
  items,
  categories,
  equipments,
  onAdd,
  onUpdate,
  onDelete,
}: {
  type: "rent" | "sale";
  items: QuoteItem[];
  equipments: Equipment[];
  categories: Category[];
  onAdd: () => void;
  onUpdate: (index: number) => (prop: string, value: UIValue) => void;
  onDelete: (index: number) => () => void;
}) => {
  return (
    <div>
      <h1 className="font-semibold text-lg mb-2">
        {type === "rent" ? "Locação" : "Venda de equipamentos"}
      </h1>
      <div className="border rounded-lg p-1">
        {items.length > 0 && (
          <table className="w-full text-sm mb-1">
            <thead className="text-left">
              <tr>
                <th className="font-semibold py-2 px-1">Categoria</th>
                <th className="font-semibold py-2 px-1">Equipamento</th>
                <th className="font-semibold py-2 px-1">Qtd</th>
                <th className="font-semibold py-2 px-1"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <Item
                  allCategories={categories}
                  allEquipments={equipments}
                  priceProp={type === "rent" ? "rent" : "price"}
                  value={item}
                  onChange={onUpdate(index)}
                  onDelete={onDelete(index)}
                  key={`${index}-${item.equipmentUuid}`}
                />
              ))}
            </tbody>
          </table>
        )}
        <button
          className="w-full py-2 text-xl hover:bg-slate-50 rounded"
          onClick={onAdd}
        >
          <FontAwesomeIcon icon="circle-plus" />
        </button>
      </div>
    </div>
  );
};

const Item = ({
  allCategories,
  allEquipments,
  priceProp,
  value,
  onChange,
  onDelete,
}: {
  allCategories: Category[];
  allEquipments: Equipment[];
  priceProp: "price" | "rent";
  value: QuoteItem;
  onChange: (prop: string, value: UIValue) => void;
  onDelete: () => void;
}) => {
  const equipment =
    _.find(allEquipments, { uuid: value.equipmentUuid }) ?? null;

  const [categoryUuid, setCategoryUuid] = useState(
    equipment?.categoryUuid ?? ""
  );
  const category = _.find(allCategories, { uuid: categoryUuid }) ?? null;

  const equipments = category
    ? _.filter(allEquipments, { categoryUuid: category.uuid })
    : allEquipments;

  const changeCategory = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryUuid(event.target.value);
    onChange("equipmentUuid", "");
  };

  return (
    <tr>
      <td>
        <select
          className="border rounded px-1 py-2 w-full"
          onChange={changeCategory}
          value={categoryUuid}
        >
          <option value=""></option>
          {allCategories.map((category) => (
            <option key={category.uuid} value={category.uuid}>
              {category.name}
            </option>
          ))}
        </select>
      </td>

      <td>
        <select
          className="border rounded px-1 py-2 w-full"
          value={equipment?.uuid ?? ""}
          onChange={(e) => onChange("equipmentUuid", e.target.value)}
        >
          <option value=""></option>
          {equipments.map((equipment) => (
            <option key={equipment.uuid} value={equipment.uuid}>
              {equipment.name} ({equipment.brand || "--"}) —{" "}
              {intToBrl(equipment[priceProp])}
            </option>
          ))}
        </select>
      </td>

      <td>
        <input
          className="border rounded py-2 pl-2 pr-1 max-w-[80px]"
          type="number"
          min="1"
          max="99999"
          value={value.amount}
          onChange={(e) => onChange("amount", e.target.value)}
        />
      </td>

      <td>
        <button className="py-2 px-4" onClick={onDelete}>
          <FontAwesomeIcon icon="trash" />
        </button>
      </td>
    </tr>
  );
};

const Fees = ({
  quote,
  onChange,
}: {
  quote: QuoteAsForm;
  onChange: (key: string) => (value: UIValue) => void;
}) => {
  return (
    <div>
      <h1 className="font-semibold text-lg mb-2">Serviços</h1>
      <div className="flex flex-col sm:flex-row text-sm gap-2">
        <div className="flex-1">
          <Fee
            label={"Monitoramento"}
            value={quote.feeMonitoring}
            onChange={onChange("feeMonitoring")}
          />
        </div>
        <div className="flex-1">
          <Fee
            label={"Instalação"}
            value={quote.feeInstall}
            onChange={onChange("feeInstall")}
          />
        </div>
        <div className="flex-1">
          <Fee
            label={"Revisão"}
            value={quote.feeReview}
            onChange={onChange("feeReview")}
          />
        </div>
      </div>
    </div>
  );
};

const Fee = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const [expanded, setExpanded] = useState(brlToInt(value) > 0);
  return (
    <>
      {!expanded && (
        <button
          className="border rounded py-5 px-2 hover:bg-slate-50 font-bold w-full"
          onClick={() => setExpanded(true)}
        >
          {label}
        </button>
      )}

      {expanded && (
        <div className="flex flex-col gap-1">
          <div className="flex">
            <div className="font-semibold flex-1">{label}</div>
            <button
              className="px-2 rounded hover:bg-slate-50"
              onClick={() => [setExpanded(false), onChange("R$ 0,00")]}
            >
              ×
            </button>
          </div>

          <MoneyInput
            className="border rounded py-2 px-3 w-full"
            value={value}
            onChange={onChange}
          />
        </div>
      )}
    </>
  );
};

const Summary = ({
  quote,
  priceMap,
  onChange,
}: {
  quote: QuoteAsForm;
  priceMap: PriceMap;
  onChange: (key: string) => (value: UIValue | QuoteItem[]) => void;
}) => {
  const [expandSale, setExpandSale] = useState(
    brlToInt(quote.discountSale) > 0
  );
  const [expandRent, setExpandRent] = useState(
    brlToInt(quote.discountRent) > 0
  );

  const baseSale = getPrice(priceMap, quote, "sale", "base");
  const finalSale = getPrice(priceMap, quote, "sale", "final");

  const baseRent = getPrice(priceMap, quote, "rent", "base");
  const finalRent = getPrice(priceMap, quote, "rent", "final");

  return (
    <div className="flex flex-col gap-2 mb-2">
      <div className="border-b-2 border-dotted">
        {expandSale && (
          <div className="flex flex-col gap-2 text-sm py-1">
            <div className="flex">
              <span className="py-1 font-semibold text-gray-500 flex-1">
                Desconto
              </span>

              <MoneyInput
                className="border rounded py-1 px-2 h-[30px] w-28 text-right"
                value={quote.discountSale}
                onChange={onChange("discountSale")}
              />
            </div>

            <div className="flex">
              <span className="py-1 font-semibold text-gray-500 flex-1">
                Parcelas
              </span>

              <select
                className="border rounded py-1 px-2 w-16"
                value={quote.maxInstallments}
                onChange={(e) => onChange("maxInstallments")(e.target.value)}
              >
                <option value="1">1x</option>
                <option value="2">2x</option>
                <option value="3">3x</option>
                <option value="4">4x</option>
                <option value="5">5x</option>
                <option value="6">6x</option>
                <option value="7">7x</option>
                <option value="8">8x</option>
                <option value="9">9x</option>
                <option value="10">10x</option>
                <option value="11">11x</option>
                <option value="12">12x</option>
              </select>
            </div>
          </div>
        )}

        <div
          className="flex py-2 cursor-pointer"
          onClick={() => setExpandSale(!expandSale)}
        >
          <div className="flex-1 font-semibold">Venda</div>
          <div>
            {baseSale === finalSale ? (
              <span className="font-bold">{intToBrl(finalSale)}</span>
            ) : (
              <span>
                <span className="font-light text-gray-500">De</span>{" "}
                <span className="font-bold">{intToBrl(baseSale)}</span>{" "}
                <span className="font-light text-gray-500">por</span>{" "}
                <span className="font-bold">{intToBrl(finalSale)}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="border-b-2 border-dotted">
        {expandRent && (
          <div className="flex text-sm py-1">
            <span className="py-1 font-semibold text-gray-500 flex-1">
              Desconto
            </span>

            <MoneyInput
              className="border rounded py-1 px-2 h-[30px] w-28 text-right"
              value={quote.discountRent}
              onChange={onChange("discountRent")}
            />
          </div>
        )}

        <div
          className="flex py-2 font-semibold cursor-pointer"
          onClick={() => setExpandRent(!expandRent)}
        >
          <div className="flex-1">Mensal</div>
          <div>
            {baseRent === finalRent ? (
              <span className="font-bold">{intToBrl(finalRent)}</span>
            ) : (
              <span>
                <span className="font-light text-gray-500">De</span>{" "}
                <span className="font-bold">{intToBrl(baseRent)}</span>{" "}
                <span className="font-light text-gray-500">por</span>{" "}
                <span className="font-bold">{intToBrl(finalRent)}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="border-b-2 border-dotted flex py-2 font-semibold">
        <div className="flex-1">Taxa</div>
        <div>
          {intToBrl(brlToInt(quote.feeInstall) + brlToInt(quote.feeReview))}
        </div>
      </div>
    </div>
  );
};

const Converter = ({
  convert,
}: {
  convert: (to: "sale" | "rent") => () => void;
}) => (
  <div className="flex gap-2 items-center border p-2 rounded">
    <div className="text-sm flex-1 text-center text-gray-600 font-extralight">
      Converter para
    </div>

    <div>
      <button
        className="text-xs py-3 px-2 bg-cyan-100 text-cyan-800 hover:bg-cyan-50 w-[100px] font-bold rounded-l"
        onClick={convert("sale")}
      >
        Venda
      </button>

      <button
        className="text-xs py-3 px-2 bg-lime-100 text-lime-800 hover:bg-lime-50 w-[100px] font-bold rounded-r"
        onClick={convert("rent")}
      >
        Locação
      </button>
    </div>
  </div>
);

const getPrice = (
  priceMap: PriceMap,
  quote: QuoteAsForm,
  type: "rent" | "sale",
  priceType: "base" | "discount" | "final"
) => {
  const items = type === "rent" ? quote.itemsRented : quote.itemsSold;
  const priceProp = type === "rent" ? "rent" : "price";
  const prices = _.chain(items)
    .filter("equipmentUuid")
    .map((item) =>
      priceMap[item.equipmentUuid]
        ? priceMap[item.equipmentUuid][priceProp] * item.amount
        : null
    )
    .valueOf();

  if (_.some(prices, (price) => price === null)) {
    const error = new Error(
      "Este orçamento não pode ser duplicado pois contém equipamentos que não estão mais disponíveis para venda ou locação."
    );
    error.name = "CustomError";
    throw error;
  }

  if (type === "rent") {
    prices.push(brlToInt(quote.feeMonitoring));
  }

  const base = _.sum(prices);

  if (priceType === "base") return base;

  const discountProp = type === "rent" ? "discountRent" : "discountSale";
  const discount = base - brlToInt(quote[discountProp]);

  if (priceType === "discount") return discount;

  const final = discount < 0 ? 0 : discount;
  return final;
};
